import { useEffect, useRef, useState } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';
import { useDialogStore, DialogKind } from '@/shared/store/dialogStore';

// Accent color + icon per dialog kind. Kept inline rather than in a
// separate icon file because each mark is one little character — easier
// to scan when it's right here.
const KIND_STYLES: Record<DialogKind, { dot: string; ring: string; label: string }> = {
  confirm: { dot: '?', ring: 'bg-primary-light text-primary',   label: 'Confirm' },
  error:   { dot: '!', ring: 'bg-rose-100 text-rose-600',       label: 'Error'   },
  warning: { dot: '!', ring: 'bg-amber-100 text-amber-600',     label: 'Warning' },
  info:    { dot: 'i', ring: 'bg-sky-100 text-sky-600',         label: 'Notice'  },
  success: { dot: '✓', ring: 'bg-emerald-100 text-emerald-600', label: 'Done'    },
  prompt:  { dot: '✎', ring: 'bg-primary-light text-primary',   label: 'Enter'   },
};

// Renders whichever dialog the store has staged. Keyboard: Enter activates the
// FOCUSED button (the browser's own behaviour), so Enter confirms because focus
// starts on the primary button, and Enter on Cancel cancels. Escape cancels. A
// prompt submits on Enter in its field, and only with text in it (an optional
// prompt also submits empty).
export function DialogHost() {
  const open = useDialogStore((s) => s.open);
  const kind = useDialogStore((s) => s.kind);
  const title = useDialogStore((s) => s.title);
  const message = useDialogStore((s) => s.message);
  const confirmLabel = useDialogStore((s) => s.confirmLabel);
  const cancelLabel = useDialogStore((s) => s.cancelLabel);
  const danger = useDialogStore((s) => s.danger);
  const input = useDialogStore((s) => s.input);
  const close = useDialogStore((s) => s.close);
  // The prompt's text lives here, seeded each time the dialog opens.
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const style = KIND_STYLES[kind];
  // A required prompt needs text; an optional one submits '' when left empty.
  const canSubmit = !!input?.optional || !!text.trim();
  const submitText = () => close(true, text.trim() ? text : '');
  const primaryRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => { if (open) setText(input?.value || ''); }, [open, input]);

  useEffect(() => {
    if (!open) return;
    // A prompt focuses its field (text selected, ready to overtype); every
    // other dialog focuses the confirm button so Enter fires it. No window
    // Enter handler: it confirmed even with focus on Cancel or Close.
    const t = setTimeout(() => {
      if (kind === 'prompt') { inputRef.current?.focus(); inputRef.current?.select(); }
      else primaryRef.current?.focus();
    }, 10);
    return () => clearTimeout(t);
  }, [open, kind]);

  // "confirm" kind is the only one that needs a Cancel button. Alert-style
  // dialogs (error/warning/info/success) just have OK — clicking backdrop
  // or Esc still resolves the promise, same as OK.
  const showCancel = kind === 'confirm' || kind === 'prompt';
  const isDanger = kind === 'confirm' ? !!danger : kind === 'error';

  return (
    <Modal
      z="z-confirm"
      open={open}
      onClose={() => close(false)}
      title={
        <div className="flex items-center gap-3">
          <span aria-hidden="true" className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-[0.8125rem] font-bold shrink-0 ${style.ring}`}>
            {style.dot}
          </span>
          <span>{title}</span>
        </div>
      }
      width="max-w-md"
      footer={
        <>
          {showCancel && (
            <Button variant="secondary" onClick={() => close(false)}>{cancelLabel || 'Cancel'}</Button>
          )}
          <Button
            ref={primaryRef}
            // The dialog's focus trap lands here first (the prompt's field
            // instead), so Enter confirms from the first moment.
            data-autofocus={kind === 'prompt' ? undefined : ''}
            variant={isDanger ? 'danger' : 'primary'}
            onClick={() => (kind === 'prompt' ? submitText() : close(true, undefined))}
            disabled={kind === 'prompt' && !canSubmit}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-[0.84375rem] text-primary-text whitespace-pre-line leading-relaxed space-y-1">
        {message}
        {kind === 'prompt' && (
          <div className="space-y-1">
            {input?.label && <div className="text-[0.75rem] text-muted">{input.label}</div>}
            <Input
              ref={inputRef}
              data-autofocus=""
              value={text}
              placeholder={input?.placeholder}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                // Enter submits the text; an empty field submits nothing (the
                // OK button is disabled for the same reason) unless the prompt
                // is optional.
                if (e.key !== 'Enter') return;
                e.preventDefault();
                if (canSubmit) submitText();
              }}
              aria-label={input?.label || title}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
